import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';

// Animation timing constants
const ANIMATION_DURATION = 1400; // How long each animation stays visible (ms)
const ANIMATION_INTERVAL = 1500; // Time between sequential animations (ms)
const ANIMATION_SPRING_DURATION = 0.8; // Framer motion animation duration (seconds)
const ANIMATION_EXIT_DURATION = 0.3; // Exit animation duration (seconds)

const PointAnimation = ({ 
  totalDiff,
  usedChallengeWords = [],
  isAI = false,
  position = 'left', // 'left' or 'right'
  className = '',
  trigger // This will change when new animations should start
}) => {
  const [animations, setAnimations] = useState([]);
  const [lastTrigger, setLastTrigger] = useState(null);
  const { t } = useLanguage();

  const positionClasses = {
    left: 'absolute top-0 left-0 z-10',
    right: 'absolute top-0 right-0 z-10'
  };

  const xOffset = position === 'left' ? -100 : 100;

  // Function to create and manage animations
  useEffect(() => {
    if (!totalDiff || totalDiff === 0 || !trigger || trigger === 0) return;

    // Prevent re-triggering for the same trigger value (multi-browser issue)
    if (trigger === lastTrigger) {
      console.log(`🚫 Animation skipped - same trigger: ${trigger}`);
      return;
    }

    console.log(`🎬 PointAnimation triggered - totalDiff: ${totalDiff}, trigger: ${trigger}, isAI: ${isAI}`);
    
    // Update the last trigger to prevent re-runs
    setLastTrigger(trigger);

    // Clear any existing animations first to prevent stacking
    setAnimations([]);
    
    const newAnimations = [];
    let animationIndex = 0;
    const baseTimestamp = Date.now();
    
    if (isAI) {
      // AI animations 
      const animation = {
        id: `ai-${baseTimestamp}-0`,
        value: totalDiff > 0 ? `+${totalDiff}` : `${totalDiff}`,
        delay: ANIMATION_INTERVAL, // Add delay for AI animation
        color: 'text-blue-200',
        label: totalDiff > 0 ? t('score.aiCorrect', 'AI Correct!') : t('score.aiPenalty', 'AI Penalty')
      };
      
      // Show AI animation after delay
      setTimeout(() => {
        setAnimations([animation]);
        console.log(`✨ AI Animation showed:`, animation.value);
      }, animation.delay);
      
      // Remove the AI animation after the duration
      setTimeout(() => {
        setAnimations([]);
      }, animation.delay + ANIMATION_DURATION);
      
    } else {
      // Player animations with bonus words
      if (totalDiff > 0) {
        // Calculate bonus points
        const wordBonus = usedChallengeWords.length * 2;
        const basePoints = totalDiff - wordBonus;
        
        // Show base points first (from correct guess)
        if (basePoints > 0) {
          newAnimations.push({
            id: `base-${baseTimestamp}-${animationIndex}`,
            value: `+${basePoints}`,
            delay: animationIndex * ANIMATION_INTERVAL,
            color: 'text-blue-200',
            label: t('score.correct', 'Correct!')
          });
          animationIndex++;
        }
        
        // Show individual word bonuses
        usedChallengeWords.forEach((word) => {
          newAnimations.push({
            id: `bonus-${baseTimestamp}-${animationIndex}`,
            value: `+2`,
            word: word,
            delay: animationIndex * ANIMATION_INTERVAL,
            color: 'text-blue-200',
            label: t('score.bonusWord', 'Bonus Word!')
          });
          animationIndex++;
        });
      } else {
        // Negative points (penalty)
        newAnimations.push({
          id: `penalty-${baseTimestamp}-0`,
          value: `${totalDiff}`,
          delay: 0,
          color: 'text-blue-200',
          label: t('score.penalty', 'Penalty')
        });
      }
      
      // Sequentially show and hide animations
      newAnimations.forEach((animation, index) => {
        // Show animation
        setTimeout(() => {
          setAnimations(prev => [...prev, animation]);
          console.log(`✨ Animation ${index + 1} showed:`, animation.value);
        }, animation.delay);
        
        // Hide animation after the duration
        setTimeout(() => {
          setAnimations(prev => prev.filter(a => a.id !== animation.id));
          console.log(`🗑️ Animation ${index + 1} hidden:`, animation.value);
        }, animation.delay + ANIMATION_DURATION);
      });
    }
  }, [trigger, totalDiff, usedChallengeWords, isAI, lastTrigger]);

  return (
    <AnimatePresence>
      {animations.map((animation, index) => (
        <motion.div
          key={animation.id}
          initial={{ opacity: 0, scale: 0.3, y: 0, x: 0 }}
          animate={{ 
            opacity: 1, 
            scale: 1, 
            y: -60,
            x: xOffset
          }}
          exit={{ opacity: 0, scale: 0.2, y: -120 }}
          transition={{ 
            duration: ANIMATION_SPRING_DURATION,
            exit: { duration: ANIMATION_EXIT_DURATION },
            type: "spring",
            bounce: 0.4
          }}
          className={`${positionClasses[position]} ${animation.color} px-4 py-2 ${className}`}
        >
          <div className="text-center">
            <div className="text-xs font-semibold text-gray-700 mb-1">
              {animation.label}
            </div>
            <div className="font-bold text-xl">
              {animation.value}
            </div>
            {/* {animation.word && (
              <div className="text-xs text-gray-600 font-medium mt-1">
                "{animation.word}"
              </div>
            )} */}
          </div>
        </motion.div>
      ))}
    </AnimatePresence>
  );
};

export default PointAnimation;
